// Shared state of the mounted Data & sets module: the derived values, the density
// result, what the pointer is over, and the update cycle. Reset on every mount.
// GPL-3.0-or-later.
import type * as S from "@dipfield/stereo";
import type { Project } from "@dipfield/project";
import { project, touched } from "../../state/store.js";
import { derive, type Derived } from "../../state/derived.js";

export type Tool = "view" | "circle" | "curved" | "freehand" | "edit";
export interface DataState {
  signal: AbortSignal;
  d: Derived;
  grid: S.DensityGrid | null; levels: number[];
  polePts: S.XY[];
  selectedSet: number; tool: Tool;
  hoverSet: number; hoverRow: number;
  tablePage: number; activeTab: string;
}
export const X: DataState = blank(new AbortController().signal);
function blank(signal: AbortSignal): DataState {
  return { signal, d: derive(project()), grid: null, levels: [], polePts: [], selectedSet: -1, tool: "view", hoverSet: -1, hoverRow: -1, tablePage: 0, activeTab: "sets" };
}
export function reset(signal: AbortSignal) { Object.assign(X, blank(signal)); }
export const P = (): Project => project();

/** Listen on `el` until the module unmounts. */
export function on<K extends keyof HTMLElementEventMap>(el: EventTarget | null, type: K | string, f: (e: any) => void, opts: AddEventListenerOptions = {}) { // eslint-disable-line @typescript-eslint/no-explicit-any
  el?.addEventListener(type, f, { ...opts, signal: X.signal });
}

// ---------- the update cycle ----------
type Hook = (table: boolean) => void;
const hooks: { recompute: Hook[]; render: Hook[]; draw: (() => void)[] } = { recompute: [], render: [], draw: [] };
export function onRecompute(f: Hook) { hooks.recompute.push(f); }
export function onRender(f: Hook) { hooks.render.push(f); }
export function onDraw(f: () => void) { hooks.draw.push(f); }
export function clearHooks() { hooks.recompute.length = hooks.render.length = hooks.draw.length = 0; }

/** Data or sets changed: recompute everything, re-render the panels, redraw, save. */
export function update(table = true) {
  X.d = derive(P());
  hooks.recompute.forEach((f) => f(table));
  hooks.render.forEach((f) => f(table));
  draw();
  touched();
}
/** Only the picture changed (a display option, the pointer). */
export function draw() { hooks.draw.forEach((f) => f()); }
/** A display setting changed: save it, re-render the panels and redraw; no recompute. */
export function viewChanged() { hooks.render.forEach((f) => f(false)); draw(); touched(); }
